import {
  type FC,
  type ReactNode,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { ChatConversationProvider } from './ChatConversationContext';
import { ChatUIStateProvider } from './ChatUIStateContext';
import { ChatActionsProvider } from './ChatActionsContext';
import type { ChatMessage, Conversation } from '../types/chat.types';
import { developerPortalSupportAgentApiRef } from '../api';
import { useStreamingChat } from '../hooks/useStreamingChat';
import { useCitedFiles } from '../hooks/useCitedFiles';
import { streamingRegistry } from '../utils/streamingRegistry';
import {
  loadMessages,
  loadConversationId,
  loadPredefinedPromptProps,
  loadResponseId,
  saveConversationToStorage,
  clearConversationFromStorage,
} from '../utils/conversationStorage';
import { generateId } from '../utils/messageFactory';
import {
  executeStreaming,
  enhanceAndUpdateCitations,
} from '../utils/streamingExecutor';
import type { PredefinedPromptProps } from '../types/predefinedPrompts';

/**
 * ChatProvider manages conversation state with optimized granular contexts.
 *
 * Design principles:
 * - Stores only completed messages in state (streaming handled separately)
 * - Exposes streaming status flags to granular contexts
 * - Streaming content passed as props directly to components
 * - Prevents re-render cascades during token streaming
 */
interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: FC<ChatProviderProps> = ({ children }) => {
  const api = useApi(developerPortalSupportAgentApiRef);
  const [completedMessages, setCompletedMessages] =
    useState<ChatMessage[]>(loadMessages);
  const [conversationId, setConversationId] =
    useState<string>(loadConversationId);

  /**
   * Tracks the last successfully completed response ID from OpenAI.
   * This is the "safe" ID that we can reference in subsequent requests.
   * NEVER updated during streaming - only after successful completion.
   */
  const [lastGoodResponseId, setLastGoodResponseId] = useState<string | null>(
    loadResponseId,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [noPreviousContextWarning, setNoPreviousContextWarning] =
    useState(false);
  const [activePredefinedPromptProps, setActivePredefinedPromptProps] =
    useState<PredefinedPromptProps | null>(loadPredefinedPromptProps);
  // currentStreamRef is used to save a ref to the current stream so we don't add stream results from an aborted stream if a new ref is created.
  const currentStreamRef = useRef(0);

  const { sendMessage: streamMessage, abort: abortStreaming } =
    useStreamingChat();
  const { enhanceCitations } = useCitedFiles();

  useEffect(() => {
    if (completedMessages.length > 0 && conversationId) {
      saveConversationToStorage(
        completedMessages,
        conversationId,
        lastGoodResponseId,
        activePredefinedPromptProps,
      );
    }
  }, [
    activePredefinedPromptProps,
    completedMessages,
    conversationId,
    lastGoodResponseId,
  ]);

  const clearConversation = useCallback(() => {
    currentStreamRef.current += 1;
    setCompletedMessages([]);
    setConversationId('');
    setStreamingMessageId(null);
    setError(null);
    setNoPreviousContextWarning(false);
    setIsLoading(false);
    setLastGoodResponseId(null);
    setActivePredefinedPromptProps(null);
    clearConversationFromStorage();
  }, []);

  const startConversation = useCallback(
    async (
      initialMessage: string,
      predefinedPromptProps?: PredefinedPromptProps,
    ) => {
      clearConversation();
      const streamRef = currentStreamRef.current;

      const userMessage: ChatMessage = {
        id: generateId(),
        sender: 'user',
        content: initialMessage,
      };

      setCompletedMessages([userMessage]);
      setActivePredefinedPromptProps(predefinedPromptProps ?? null);
      setIsLoading(true);

      const agentMessageId = generateId();
      setStreamingMessageId(agentMessageId);

      try {
        const result = await executeStreaming({
          messageContent: initialMessage,
          agentMessageId,
          lastResponseId: null,
          predefinedPromptProps: predefinedPromptProps
            ? {
                ...predefinedPromptProps,
                isInitialPredefinedPromptRequest: true,
              }
            : undefined,
          streamMessage,
          setError,
          enhanceCitations,
        });

        // If a user starts a new chat while an old chat is still streaming, the currentStreamRef.current will be incremented.
        // If we get a streaming result from the old stream, we want to return here.
        if (currentStreamRef.current !== streamRef) {
          return;
        }

        if (result.success && result.message) {
          if (result.conversationId) {
            setConversationId(result.conversationId);
          }

          setCompletedMessages(prev => [...prev, result.message!]);

          // Promote completed response ID to last good on successful completion
          // This ID is now safe to reference in future requests
          if (result.responseId) {
            setLastGoodResponseId(result.responseId);
          }

          await enhanceAndUpdateCitations(
            result.message,
            enhanceCitations,
            setCompletedMessages,
          );
        }
      } finally {
        if (currentStreamRef.current === streamRef) {
          setStreamingMessageId(null);
          setIsLoading(false);
        }
      }
    },
    [streamMessage, enhanceCitations, clearConversation],
  );

  const addMessage = useCallback(
    async (
      message: Omit<ChatMessage, 'id'>,
      predefinedPromptProps?: PredefinedPromptProps,
    ) => {
      if (!conversationId) return;

      const effectivePredefinedPromptProps =
        predefinedPromptProps ?? activePredefinedPromptProps ?? undefined;

      if (predefinedPromptProps) {
        setActivePredefinedPromptProps(predefinedPromptProps);
      }

      const newMessage: ChatMessage = {
        ...message,
        id: generateId(),
      };

      setNoPreviousContextWarning(false);
      setCompletedMessages(prev => [...prev, newMessage]);

      if (message.sender !== 'user') return;

      const streamRef = currentStreamRef.current + 1;
      currentStreamRef.current = streamRef;

      setIsLoading(true);
      const agentMessageId = generateId();
      setStreamingMessageId(agentMessageId);

      try {
        const result = await executeStreaming({
          messageContent: newMessage.content,
          conversationId,
          agentMessageId,
          lastResponseId: lastGoodResponseId,
          predefinedPromptProps: effectivePredefinedPromptProps,
          streamMessage,
          setError,
          enhanceCitations,
        });

        // If a user starts a new chat while an old chat is still streaming, the currentStreamRef.current will be incremented.
        // If we get a streaming result from the old stream, we want to return here.
        if (currentStreamRef.current !== streamRef) {
          return;
        }

        if (result.success && result.message) {
          if (result.conversationId) {
            setConversationId(result.conversationId);
          }

          setCompletedMessages(prev => [...prev, result.message!]);

          // Promote completed response ID to last good on successful completion
          if (result.responseId) {
            setLastGoodResponseId(result.responseId);
          }

          await enhanceAndUpdateCitations(
            result.message,
            enhanceCitations,
            setCompletedMessages,
          );
        }
      } finally {
        if (currentStreamRef.current === streamRef) {
          setStreamingMessageId(null);
          setIsLoading(false);
        }
      }
    },
    [
      activePredefinedPromptProps,
      conversationId,
      streamMessage,
      enhanceCitations,
      lastGoodResponseId,
    ],
  );

  const applyLoadedConversation = useCallback(
    (loadedConversationId: string, conversation: Conversation) => {
      const loadedMessages = conversation.messages.map((message, index) => ({
        id: `${loadedConversationId}-${index}`,
        sender: message.sender,
        content: message.content,
      }));

      setCompletedMessages(loadedMessages);
      setConversationId(loadedConversationId);

      const shouldShowNoPreviousContextWarning = Boolean(
        conversation.lastResponseId && !conversation.previousContextExists,
      );

      setNoPreviousContextWarning(shouldShowNoPreviousContextWarning);
      setLastGoodResponseId(
        shouldShowNoPreviousContextWarning ? null : conversation.lastResponseId,
      );
    },
    [],
  );

  const loadConversation = useCallback(
    (loadedConversationId: string, conversation: Conversation) => {
      clearConversation();
      applyLoadedConversation(loadedConversationId, conversation);
    },
    [applyLoadedConversation, clearConversation],
  );

  const loadConversationFromHistory = useCallback(
    async (loadedConversationId: string) => {
      currentStreamRef.current += 1;
      setCompletedMessages([]);
      setConversationId('');
      setStreamingMessageId(null);
      setError(null);
      setNoPreviousContextWarning(false);
      setLastGoodResponseId(null);
      setActivePredefinedPromptProps(null);
      clearConversationFromStorage();
      setIsLoading(true);

      try {
        const conversation = await api.getConversation(loadedConversationId);
        applyLoadedConversation(loadedConversationId, conversation);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to load conversation',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [api, applyLoadedConversation],
  );

  const retry = useCallback(async () => {
    throw new Error('Retry functionality not yet implemented');
  }, []);

  const abort = useCallback(() => {
    abortStreaming();

    const currentStreamingId = streamingMessageId;

    setStreamingMessageId(null);
    setIsLoading(false);
    setError(null);

    if (currentStreamingId) {
      streamingRegistry.error(
        currentStreamingId,
        new Error('User aborted the request'),
      );
    }
  }, [abortStreaming, streamingMessageId]);

  const conversationData = useMemo(
    () => ({
      messages: completedMessages,
      conversationId,
    }),
    [completedMessages, conversationId],
  );

  const uiStateData = useMemo(
    () => ({
      isStreaming: streamingMessageId !== null,
      streamingMessageId,
      error,
      isLoading,
      noPreviousContextWarning,
    }),
    [streamingMessageId, error, isLoading, noPreviousContextWarning],
  );

  const actionsData = useMemo(
    () => ({
      startConversation,
      addMessage,
      clearConversation,
      loadConversation,
      loadConversationFromHistory,
      retry,
      abort,
    }),
    [
      startConversation,
      addMessage,
      clearConversation,
      loadConversation,
      loadConversationFromHistory,
      retry,
      abort,
    ],
  );

  return (
    <ChatConversationProvider
      messages={conversationData.messages}
      conversationId={conversationData.conversationId}
    >
      <ChatUIStateProvider
        isStreaming={uiStateData.isStreaming}
        streamingMessageId={uiStateData.streamingMessageId}
        error={uiStateData.error}
        isLoading={uiStateData.isLoading}
        noPreviousContextWarning={uiStateData.noPreviousContextWarning}
      >
        <ChatActionsProvider
          startConversation={actionsData.startConversation}
          addMessage={actionsData.addMessage}
          clearConversation={actionsData.clearConversation}
          loadConversation={actionsData.loadConversation}
          loadConversationFromHistory={actionsData.loadConversationFromHistory}
          retry={actionsData.retry}
          abort={actionsData.abort}
        >
          {children}
        </ChatActionsProvider>
      </ChatUIStateProvider>
    </ChatConversationProvider>
  );
};
